import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { inr } from "@/lib/format";
import { Plus, Pencil, Trash2, Loader2, X, Upload } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const CATEGORIES = ["Hoodies", "Oversized Tees", "Cargo Pants", "Outerwear", "Accessories"];
const EMPTY = { name: "", description: "", category: "Hoodies", price: "", compare_at: "", images: [], sizes: "S:10,M:10,L:10,XL:5", colors: "Black", featured: false, fabric: "" };

function parseSizes(str) {
  return str.split(",").map((s) => s.trim()).filter(Boolean).map((pair) => {
    const [size, stock] = pair.split(":");
    return { size: (size || "").trim(), stock: parseInt(stock, 10) || 0 };
  });
}
function sizesToStr(arr) {
  return (arr || []).map((s) => `${s.size}:${s.stock}`).join(",");
}

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [confirmAll, setConfirmAll] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name, description: p.description, category: p.category,
      price: String(p.price), compare_at: p.compare_at ? String(p.compare_at) : "",
      images: (p.images || []), sizes: sizesToStr(p.sizes),
      colors: (p.colors || []).join(", "), featured: p.featured, fabric: p.fabric || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name || !form.price) return toast.error("Name and price are required");
    setSaving(true);
    const payload = {
      name: form.name, description: form.description, category: form.category,
      price: parseFloat(form.price), compare_at: form.compare_at ? parseFloat(form.compare_at) : null,
      images: (form.images || []).filter(Boolean),
      sizes: parseSizes(form.sizes),
      colors: form.colors.split(",").map((s) => s.trim()).filter(Boolean),
      featured: form.featured, fabric: form.fabric,
    };
    try {
      if (editing) await api.put(`/admin/products/${editing.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(editing ? "Product updated" : "Product created");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    try {
      await api.delete(`/admin/products/${toDelete.id}`);
      toast.success("Product deleted");
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const delAll = async () => {
    try {
      await api.delete("/admin/products");
      toast.success("All products deleted");
      setConfirmAll(false);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setForm((f) => ({ ...f, images: [...(f.images || []), data.url] }));
      }
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeImage = (idx) => setForm((f) => ({ ...f, images: (f.images || []).filter((_, i) => i !== idx) }));

  return (
    <div data-testid="admin-products">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-xl font-bold uppercase tracking-tight">Products ({products.length})</h2>
        <div className="flex gap-2">
          {products.length > 0 && (
            <button onClick={() => setConfirmAll(true)} data-testid="delete-all-products-btn" className="inline-flex items-center gap-2 rounded-full border border-red-500/40 text-red-400 px-4 py-2 text-xs font-bold uppercase tracking-wide hover:bg-red-500/10 active:scale-95 transition">
              <Trash2 className="h-4 w-4" /> Delete All
            </button>
          )}
          <button onClick={openNew} data-testid="add-product-btn" className="inline-flex items-center gap-2 rounded-full bg-brand text-black px-4 py-2 text-xs font-bold uppercase tracking-wide active:scale-95 transition">
            <Plus className="h-4 w-4" /> Add Product
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
      ) : (
        <div className="border border-zinc-800 divide-y divide-zinc-900">
          {products.map((p) => (
            <div key={p.id} className="flex items-center gap-4 p-3" data-testid={`admin-product-${p.id}`}>
              <img src={p.images?.[0]} alt={p.name} className="h-14 w-12 object-cover bg-zinc-900" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{p.name}</p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{p.category} · {p.total_stock} in stock {p.featured && "· featured"}</p>
              </div>
              <span className="font-mono text-sm">{inr(p.price)}</span>
              <div className="flex gap-1">
                <button onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`} className="p-2 text-zinc-400 hover:text-brand"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => setToDelete(p)} data-testid={`delete-product-${p.id}`} className="p-2 text-zinc-400 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
          <DialogHeader><DialogTitle className="font-display uppercase tracking-tight">{editing ? "Edit Product" : "New Product"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Name</Label><Input data-testid="product-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1" /></div>
            <div><Label className="text-xs">Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1" rows={2} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Category</Label>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="product-category" className="w-full mt-1 bg-zinc-900 border border-zinc-800 rounded-md h-9 px-2 text-sm">
                  {CATEGORIES.map((c) => <option key={c} value={c} className="bg-zinc-900">{c}</option>)}
                </select>
              </div>
              <div><Label className="text-xs">Fabric</Label><Input value={form.fabric} onChange={(e) => setForm({ ...form, fabric: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Price (₹)</Label><Input data-testid="product-price" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1" /></div>
              <div><Label className="text-xs">Compare At (₹)</Label><Input type="number" value={form.compare_at} onChange={(e) => setForm({ ...form, compare_at: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1" /></div>
            </div>
            <div>
              <Label className="text-xs">Product Images</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {(form.images || []).map((src, i) => (
                  <div key={i} className="relative h-20 w-16">
                    <img src={src} alt="" className="h-full w-full object-cover bg-zinc-900 border border-zinc-800" />
                    <button type="button" onClick={() => removeImage(i)} data-testid={`remove-image-${i}`} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-image-btn" className="h-20 w-16 border border-dashed border-zinc-700 flex flex-col items-center justify-center gap-1 text-zinc-500 hover:border-brand hover:text-brand transition disabled:opacity-60">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4" /><span className="text-[9px] uppercase tracking-widest">Upload</span></>}
                </button>
              </div>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" multiple hidden onChange={handleUpload} data-testid="product-image-input" />
              <p className="mt-1.5 font-mono text-[10px] uppercase tracking-widest text-zinc-600">PNG, JPG, GIF or WEBP · up to 5MB each</p>
            </div>
            <div><Label className="text-xs">Sizes (e.g. S:10,M:8,L:3)</Label><Input data-testid="product-sizes" value={form.sizes} onChange={(e) => setForm({ ...form, sizes: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1" /></div>
            <div><Label className="text-xs">Colors (comma separated)</Label><Input value={form.colors} onChange={(e) => setForm({ ...form, colors: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1" /></div>
            <div className="flex items-center justify-between"><Label className="text-xs">Featured on homepage</Label><Switch checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} data-testid="product-featured" /></div>
          </div>
          <DialogFooter>
            <button onClick={save} disabled={saving} data-testid="save-product-btn" className="w-full rounded-full bg-brand text-black py-2.5 text-sm font-bold uppercase tracking-wide active:scale-95 transition disabled:opacity-60 flex items-center justify-center gap-2">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? "Save Changes" : "Create Product")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent className="bg-zinc-950 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this product?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">"{toDelete?.name}" will be permanently removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-900 border-zinc-800">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={del} data-testid="confirm-delete-btn" className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmAll} onOpenChange={setConfirmAll}>
        <AlertDialogContent className="bg-zinc-950 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete ALL products?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">Every product will be permanently removed and the store will show 0 items. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-900 border-zinc-800">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={delAll} data-testid="confirm-delete-all-btn" className="bg-red-500 hover:bg-red-600">Delete Everything</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
